/**
 * Таблица броней: кто где живёт, когда выезжает, сколько стоило.
 *
 * Сверху — сводка по выборке (брони, ночи, суммы по валютам), чтобы
 * ответ на «сколько потратили на жильё в Варшаве за октябрь» был виден без
 * выгрузки в таблицу.
 *
 * Черновики — брони, записанные сами со страницы площадки, — стоят в общей
 * таблице с меткой «Заполнить», а над ней плашка со счётчиком переключает
 * таблицу на них одних.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BedDouble,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  FileText,
  RotateCcw,
  UserRound,
  ExternalLink,
  MessageCircle,
  Pencil,
  Plus,
  Search,
  Trash2,
  Wallet,
} from 'lucide-react';
import type {
  StayAccountDto,
  StayBookingDto,
  StayBookingFacetsDto,
  StayBookingListDto,
  StayPersonDto,
  StayPropertyStatus,
} from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { cn } from '@/lib/utils';
import { BookingDialog, type BookingPrefill } from './BookingDialog.tsx';
import { useIsMobile } from '../../lib/useMedia.ts';
import { EmptyState, INPUT, PlatformMark, RatingBadge, StatusPill } from './bits.tsx';
import {
  PROPERTY_STATUS,
  accountLabel,
  addDaysIso,
  formatMoney,
  formatRange,
  nightsLabel,
  periodRange,
  stayPhase,
  todayIso,
} from './meta.ts';

type Phase = 'active' | 'week' | 'month' | 'range' | 'past' | 'all';

const PHASES: Array<{ key: Phase; label: string }> = [
  { key: 'active', label: 'Сейчас и впереди' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
  { key: 'range', label: 'Период' },
  { key: 'past', label: 'Прошедшие' },
  { key: 'all', label: 'Все' },
];

/** Статус квартиры в фильтре: «не проверена» — это и «без отметки», и не из базы. */
const PROPERTY_FILTER: Array<{ key: StayPropertyStatus; label: string }> = [
  { key: 'VERIFIED', label: PROPERTY_STATUS.VERIFIED.label },
  { key: 'NEUTRAL', label: 'Не проверенная' },
  { key: 'BLACKLISTED', label: PROPERTY_STATUS.BLACKLISTED.label },
];

interface Props {
  accounts: StayAccountDto[];
  people: StayPersonDto[];
  /** Внешний запрос «записать бронь» (из базы квартир или со страницы сайта). */
  prefill: BookingPrefill | null;
  onPrefillUsed: () => void;
  /** Открыть ссылку во встроенном браузере — в аккаунте брони, если он указан. */
  onOpenUrl?: (url: string, accountId?: string | null) => void;
}

export function BookingsPanel({ accounts, people, prefill, onPrefillUsed, onOpenUrl }: Props) {
  const { api, can } = useSession();
  const [data, setData] = useState<StayBookingListDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('active');
  /** Сдвиг недели или месяца от текущих: 0 — эта, -1 — прошлая. */
  const [offset, setOffset] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(() => addDaysIso(todayIso(), -30));
  const [rangeTo, setRangeTo] = useState(todayIso);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [worker, setWorker] = useState('');
  const [workerQuery, setWorkerQuery] = useState('');
  const [accountId, setAccountId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [propertyStatus, setPropertyStatus] = useState<StayPropertyStatus | ''>('');
  const [facets, setFacets] = useState<StayBookingFacetsDto>({ countries: [], cities: [] });
  const [showCancelled, setShowCancelled] = useState(false);
  const [onlyDrafts, setOnlyDrafts] = useState(false);
  const mobile = useIsMobile();
  /** Телефон: фильтры, кроме «кто живёт», — за кнопкой. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dialog, setDialog] = useState<{ booking?: StayBookingDto; prefill?: BookingPrefill } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => setWorkerQuery(worker.trim()), 250);
    return () => clearTimeout(t);
  }, [worker]);

  /** Границы периода для API: `to` — день после последнего. */
  const period = useMemo((): { from?: string; to?: string; label?: string } => {
    const today = todayIso();
    switch (phase) {
      case 'active':
        return { from: today };
      case 'past':
        return { to: today };
      case 'week':
      case 'month':
        return periodRange(phase, offset);
      case 'range':
        return rangeFrom && rangeTo && rangeTo >= rangeFrom ? { from: rangeFrom, to: addDaysIso(rangeTo, 1) } : {};
      default:
        return {};
    }
  }, [phase, offset, rangeFrom, rangeTo]);

  const filtered = !!(query || workerQuery || accountId || managerId || country || city || propertyStatus || showCancelled);
  /** Сколько фильтров за кнопкой «Фильтры» включено (телефон). */
  const activeFilterCount = [query, accountId, managerId, country, city, propertyStatus, showCancelled].filter(Boolean).length;
  const resetFilters = () => {
    setQ('');
    setWorker('');
    setAccountId('');
    setManagerId('');
    setCountry('');
    setCity('');
    setPropertyStatus('');
    setShowCancelled(false);
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.stayBookings(
        onlyDrafts
          ? // Черновики — все сразу, без периода и фильтров: их надо разобрать.
            { limit: 200, draft: 'true' }
          : {
              limit: 200,
              ...(period.from ? { from: period.from } : {}),
              ...(period.to ? { to: period.to } : {}),
              ...(query ? { q: query } : {}),
              ...(workerQuery ? { worker: workerQuery } : {}),
              ...(accountId ? { accountId } : {}),
              ...(managerId ? { managerId } : {}),
              ...(country ? { country } : {}),
              ...(city ? { city } : {}),
              ...(propertyStatus ? { propertyStatus } : {}),
              ...(showCancelled ? {} : { status: 'CONFIRMED' as const }),
            },
      );
      setData(res);
      if (onlyDrafts && res.draftCount === 0) setOnlyDrafts(false);
    } catch (err) {
      setError(describeError(err));
    }
    // Списки стран и городов меняются вместе с бронями — обновляем рядом.
    api.stayBookingFacets().then(setFacets).catch(() => undefined);
  }, [api, period, query, workerQuery, accountId, managerId, country, city, propertyStatus, showCancelled, onlyDrafts]);

  /** Города выбранной страны; без страны — все. */
  const cityOptions = useMemo(
    () => facets.cities.filter((c) => !country || c.country === country || c.country === null),
    [facets, country],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (prefill) {
      setDialog({ prefill });
      onPrefillUsed();
    }
  }, [prefill, onPrefillUsed]);

  // «Прошедшие» по пересечению с периодом захватывают и текущие — отрезаем.
  const items = useMemo(() => {
    const list = data?.items ?? [];
    if (onlyDrafts) return list;
    if (phase === 'past') return list.filter((b) => stayPhase(b.checkIn, b.checkOut) === 'past');
    // Впереди и в выбранном периоде — по дате заезда, ранние сверху.
    if (phase === 'active' || phase === 'week' || phase === 'month' || phase === 'range') {
      return [...list].sort((a, b) => a.checkIn.localeCompare(b.checkIn));
    }
    return list;
  }, [data, phase, onlyDrafts]);

  /**
   * Ссылка площадки — во встроенный браузер того аккаунта, с которого
   * бронировали (квитанция и переписка видны только под ним); в обычном
   * браузере — новой вкладкой.
   */
  const open = (url: string, b: StayBookingDto) =>
    onOpenUrl ? onOpenUrl(url, b.account?.id) : window.open(url, '_blank', 'noopener,noreferrer');

  const livingNow = items.filter((b) => b.status === 'CONFIRMED' && stayPhase(b.checkIn, b.checkOut) === 'current').length;

  const remove = async (b: StayBookingDto) => {
    if (!window.confirm(`Удалить бронь «${b.title}»${b.workerName ? ` (${b.workerName})` : ''}? Отменённую бронь лучше пометить отменой — удаление стирает историю.`)) return;
    try {
      await api.deleteStayBooking(b.id);
      void load();
    } catch (err) {
      setError(describeError(err));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* ── Сводка ── */}
      <div className={cn('grid', mobile ? 'grid-cols-3 gap-2' : 'gap-3 sm:grid-cols-2 xl:grid-cols-4')}>
        <Stat compact={mobile} icon={<CalendarRange className="size-4" />} label={mobile ? 'Броней' : 'Броней в выборке'} value={String(data?.totalCount ?? '—')} />
        <Stat compact={mobile} icon={<BedDouble className="size-4" />} label={mobile ? 'Живут' : 'Сейчас живут'} value={String(livingNow)} accent />
        <Stat compact={mobile} icon={<CalendarRange className="size-4" />} label="Ночей" value={data ? String(data.totalNights) : '—'} />
        <Stat
          compact={mobile}
          className={mobile ? 'col-span-3' : undefined}
          icon={<Wallet className="size-4" />}
          label="Сумма"
          value={
            data && data.totals.length > 0
              ? data.totals.map((t) => formatMoney(t.amountMinor, t.currency)).join(' · ')
              : '—'
          }
        />
      </div>

      {/* ── Черновики ── */}
      {data && data.draftCount > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-warning/40 bg-warning/10 px-3.5 py-2.5 text-[13px] text-heading">
          <CircleAlert className="size-4 flex-none text-warning" />
          <p className="min-w-0 flex-1">
            {onlyDrafts ? (
              'Черновики: брони, записанные автоматически со страниц площадок. Укажите, кто подбирал и кто заселяется.'
            ) : (
              <>
                <span className="font-semibold">{draftsLabel(data.draftCount)}</span>
                {isOne(data.draftCount) ? ' записана автоматически и ждёт заполнения' : ' записаны автоматически и ждут заполнения'}
              </>
            )}
          </p>
          <Button size="sm" variant={onlyDrafts ? 'outline' : 'default'} className="h-7 rounded-full" onClick={() => setOnlyDrafts(!onlyDrafts)}>
            {onlyDrafts ? 'Все брони' : 'Показать'}
          </Button>
        </div>
      )}

      {/* ── Период ── */}
      <div className={cn('flex flex-wrap items-center gap-2', onlyDrafts && 'pointer-events-none opacity-40')} aria-disabled={onlyDrafts}>
        <div className="flex flex-wrap rounded-full bg-secondary p-1">
          {PHASES.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                setPhase(p.key);
                setOffset(0);
              }}
              className={cn(
                'rounded-full px-3 py-1 text-[12px] font-medium transition-colors',
                phase === p.key ? 'bg-card text-heading shadow-sm' : 'text-muted-foreground hover:text-heading',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        {(phase === 'week' || phase === 'month') && (
          <div className="flex items-center gap-1 rounded-full border border-border bg-card px-1 py-0.5 shadow-sm">
            <PeriodArrow label={phase === 'week' ? 'Предыдущая неделя' : 'Предыдущий месяц'} onClick={() => setOffset(offset - 1)}>
              <ChevronLeft className="size-4" />
            </PeriodArrow>
            <span className="min-w-[150px] px-1 text-center text-[12px] font-medium text-heading">{period.label}</span>
            <PeriodArrow label={phase === 'week' ? 'Следующая неделя' : 'Следующий месяц'} onClick={() => setOffset(offset + 1)}>
              <ChevronRight className="size-4" />
            </PeriodArrow>
            {offset !== 0 && (
              <button
                type="button"
                onClick={() => setOffset(0)}
                className="rounded-full border-0 bg-transparent px-2 py-0.5 text-[11px] font-medium text-brand hover:bg-secondary"
              >
                {phase === 'week' ? 'эта неделя' : 'этот месяц'}
              </button>
            )}
          </div>
        )}
        {phase === 'range' && (
          <div className="flex items-center gap-1.5 text-[12px] text-soft">
            с
            <input type="date" className={cn(INPUT, 'h-8 w-auto')} value={rangeFrom} max={rangeTo} onChange={(e) => setRangeFrom(e.target.value)} />
            по
            <input type="date" className={cn(INPUT, 'h-8 w-auto')} value={rangeTo} min={rangeFrom} onChange={(e) => setRangeTo(e.target.value)} />
          </div>
        )}
        {can('stays.book') && (
          <Button size="sm" className="ml-auto rounded-full" onClick={() => setDialog({})}>
            <Plus className="size-4" /> Бронь
          </Button>
        )}
      </div>

      {/* ── Фильтры ── */}
      <div className={cn('flex flex-wrap items-center gap-2', onlyDrafts && 'pointer-events-none opacity-40')} aria-disabled={onlyDrafts}>
        <div className="relative min-w-[200px] flex-1">
          <UserRound className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className={cn(INPUT, 'pl-9', worker && 'border-brand')}
            placeholder="Кто живёт: имя работника"
            value={worker}
            onChange={(e) => setWorker(e.target.value)}
          />
        </div>
        {mobile && (
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            className={cn(
              'inline-flex h-9 flex-none items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium',
              filtered || filtersOpen ? 'border-brand bg-primary/5 text-brand' : 'border-border bg-card text-soft',
            )}
          >
            Фильтры
            {activeFilterCount > 0 && (
              <span className="grid size-5 place-items-center rounded-full bg-primary text-[11px] text-primary-foreground">{activeFilterCount}</span>
            )}
          </button>
        )}
        {(!mobile || filtersOpen) && (
        <>
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input className={cn(INPUT, 'pl-9', q && 'border-brand')} placeholder="Квартира, город, код брони" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <FilterSelect value={managerId} onChange={setManagerId} active={!!managerId}>
          <option value="">Все менеджеры</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          value={country}
          active={!!country}
          onChange={(v) => {
            setCountry(v);
            setCity('');
          }}
        >
          <option value="">Все страны</option>
          {facets.countries.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </FilterSelect>
        <FilterSelect value={city} onChange={setCity} active={!!city}>
          <option value="">Все города</option>
          {cityOptions.map((c) => (
            <option key={c.city} value={c.city}>
              {c.city}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={propertyStatus} onChange={(v) => setPropertyStatus(v as StayPropertyStatus | '')} active={!!propertyStatus}>
          <option value="">Любая квартира</option>
          {PROPERTY_FILTER.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={accountId} onChange={setAccountId} active={!!accountId}>
          <option value="">Все аккаунты</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {accountLabel(a)}
            </option>
          ))}
        </FilterSelect>
        <label className="flex items-center gap-1.5 text-[12px] text-soft">
          <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
          отменённые
        </label>
        {filtered && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-8 items-center gap-1 rounded-full border-0 bg-transparent px-2.5 text-[12px] font-medium text-brand hover:bg-secondary"
          >
            <RotateCcw className="size-3.5" /> Сбросить
          </button>
        )}
        </>
        )}
      </div>

      {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

      {/* ── Таблица ── */}
      {data && items.length === 0 ? (
        <EmptyState icon={<BedDouble className="size-6" />} title="Броней нет">
          {phase === 'active' && !filtered
            ? 'Записанные брони появятся здесь. Бронь записывается сама со страницы брони во вкладке «Браузер» или кнопкой «Бронь».'
            : 'В этой выборке ничего не нашлось — поменяйте период или фильтры.'}
        </EmptyState>
      ) : mobile ? (
        <div className="flex flex-col gap-2.5">
          {items.map((b) => (
            <BookingCard key={b.id} b={b} onOpen={(url) => open(url, b)} onEdit={() => setDialog({ booking: b })} onRemove={() => void remove(b)} />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
          <table className="w-full min-w-[920px] border-separate border-spacing-0 text-left text-[13px]">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                <th className="sticky top-0 border-b border-border bg-card px-4 py-2.5">Квартира</th>
                <th className="sticky top-0 border-b border-border bg-card px-3 py-2.5">Даты</th>
                <th className="sticky top-0 border-b border-border bg-card px-3 py-2.5">Кто живёт</th>
                <th className="sticky top-0 border-b border-border bg-card px-3 py-2.5">Подбирал</th>
                <th className="sticky top-0 border-b border-border bg-card px-3 py-2.5 text-right">Стоимость</th>
                <th className="sticky top-0 border-b border-border bg-card px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const ph = stayPhase(b.checkIn, b.checkOut);
                const cancelled = b.status === 'CANCELLED';
                return (
                  <tr key={b.id} className={cn('group transition-colors hover:bg-accent/60', cancelled && 'opacity-55')}>
                    <td className="border-b border-border/60 px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {b.account ? <PlatformMark platform={b.account.platform} /> : <span className="size-6" />}
                        <div className="min-w-0">
                          {b.url && onOpenUrl ? (
                            <button
                              type="button"
                              title={`Открыть квартиру${b.account ? ` в браузере аккаунта «${b.account.title}»` : ''}`}
                              onClick={() => onOpenUrl(b.url!, b.account?.id)}
                              className={cn(
                                'block max-w-[300px] truncate border-0 bg-transparent p-0 text-left font-medium text-heading hover:text-brand hover:underline',
                                cancelled && 'line-through',
                              )}
                            >
                              {b.title}
                            </button>
                          ) : (
                            <p className={cn('max-w-[300px] truncate font-medium text-heading', cancelled && 'line-through')}>{b.title}</p>
                          )}
                          <p className="mt-0.5 flex items-center gap-2 text-[12px] text-muted-foreground">
                            {[b.city, b.country].filter(Boolean).join(', ') || '—'}
                            {b.property && <StatusPill status={b.property.status} />}
                            {b.property?.rating != null && <RatingBadge rating={b.property.rating} />}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="border-b border-border/60 px-3 py-3">
                      <p className="whitespace-nowrap text-heading">{formatRange(b.checkIn, b.checkOut)}</p>
                      <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                        {nightsLabel(b.nights)}
                        {!cancelled && ph === 'current' && (
                          <span className="rounded-full bg-success/12 px-1.5 text-[11px] font-medium text-success">живут</span>
                        )}
                        {cancelled && <span className="text-destructive">отменена</span>}
                      </p>
                    </td>
                    <td className="border-b border-border/60 px-3 py-3 text-heading">
                      {b.draft ? (
                        <DraftPill onClick={b.canEdit ? () => setDialog({ booking: b }) : undefined} />
                      ) : (
                        b.workerName
                      )}
                    </td>
                    <td className="border-b border-border/60 px-3 py-3 text-soft">{b.manager?.displayName ?? '—'}</td>
                    <td className="border-b border-border/60 px-3 py-3 text-right font-medium tabular-nums text-heading">
                      {formatMoney(b.priceMinor, b.currency)}
                      {b.account && <p className="mt-0.5 text-[11px] font-normal text-muted-foreground">{b.account.title}</p>}
                    </td>
                    <td className="border-b border-border/60 px-3 py-3">
                      <div className="flex justify-end gap-0.5 opacity-70 transition-opacity group-hover:opacity-100">
                        {b.url && (
                          <RowAction label="Открыть квартиру" onClick={() => open(b.url!, b)}>
                            <ExternalLink className="size-4" />
                          </RowAction>
                        )}
                        {b.bookingUrl && (
                          <RowAction label="Открыть бронь на сайте" onClick={() => open(b.bookingUrl!, b)}>
                            <FileText className="size-4" />
                          </RowAction>
                        )}
                        {b.chatUrl && (
                          <RowAction label="Переписка с хозяином" onClick={() => open(b.chatUrl!, b)}>
                            <MessageCircle className="size-4" />
                          </RowAction>
                        )}
                        {b.canEdit && (
                          <>
                            <RowAction label="Изменить" onClick={() => setDialog({ booking: b })}>
                              <Pencil className="size-4" />
                            </RowAction>
                            <RowAction label="Удалить" danger onClick={() => void remove(b)}>
                              <Trash2 className="size-4" />
                            </RowAction>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BookingDialog
        open={dialog !== null}
        booking={dialog?.booking ?? null}
        prefill={dialog?.prefill ?? null}
        accounts={accounts}
        people={people}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          void load();
        }}
      />
    </div>
  );
}

/** 1, 21, 101 — «одна» форма: «1 бронь записана», но «11 броней записаны». */
const isOne = (n: number): boolean => n % 10 === 1 && n % 100 !== 11;

/** «3 брони», «5 броней», «1 бронь». */
function draftsLabel(n: number): string {
  const tail = isOne(n) ? 'бронь' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'брони' : 'броней';
  return `${n} ${tail}`;
}

/** Метка черновика в строке; кнопка — у тех, кто может его дозаполнить. */
function DraftPill({ onClick }: { onClick?: () => void }) {
  const cls = 'inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[11px] font-semibold text-warning';
  return onClick ? (
    <button type="button" onClick={onClick} className={cn(cls, 'transition-colors hover:bg-warning/20')}>
      <Pencil className="size-3" /> Заполнить
    </button>
  ) : (
    <span className={cls}>Заполнить</span>
  );
}

function PeriodArrow({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="grid size-7 place-items-center rounded-full border-0 bg-transparent p-0 text-soft hover:bg-secondary hover:text-heading"
    >
      {children}
    </button>
  );
}

/** Выпадающий фильтр; выбранный подсвечен, чтобы было видно, что выборка сужена. */
function FilterSelect({
  value,
  onChange,
  active,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <select
      className={cn(INPUT, 'w-auto max-w-[220px] pr-8', active && 'border-brand bg-primary/5 text-heading')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {children}
    </select>
  );
}

function Stat({
  icon,
  label,
  value,
  accent,
  compact,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: boolean;
  /** Телефон: без значка, плотнее — три в ряд. */
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center rounded-2xl border border-border bg-card shadow-sm',
        compact ? 'gap-2 px-3 py-2' : 'gap-3 px-4 py-3',
        accent && 'border-success/30 bg-success/5',
        className,
      )}
    >
      {!compact && (
        <span className={cn('grid size-9 flex-none place-items-center rounded-xl', accent ? 'bg-success/15 text-success' : 'bg-primary/10 text-brand')}>{icon}</span>
      )}
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">{label}</p>
        <p className={cn('truncate font-semibold tabular-nums text-heading', compact ? 'text-[15px]' : 'text-[17px]')}>{value}</p>
      </div>
    </div>
  );
}

/**
 * Бронь карточкой — телефон, где таблица в 920 px не помещается. Порядок —
 * как читают на ходу: где и когда, кто живёт, сколько стоило; действия внизу.
 */
function BookingCard({
  b,
  onOpen,
  onEdit,
  onRemove,
}: {
  b: StayBookingDto;
  onOpen: (url: string) => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const cancelled = b.status === 'CANCELLED';
  const ph = stayPhase(b.checkIn, b.checkOut);
  return (
    <div className={cn('rounded-2xl border border-border bg-card p-3.5 shadow-sm', cancelled && 'opacity-60')}>
      <div className="flex items-start gap-2.5">
        {b.account ? <PlatformMark platform={b.account.platform} /> : <span className="size-6" />}
        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-[15px] font-semibold text-heading', cancelled && 'line-through')}>{b.title}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
            {[b.city, b.country].filter(Boolean).join(', ') || 'город не указан'}
            {b.property && <StatusPill status={b.property.status} />}
            {b.property?.rating != null && <RatingBadge rating={b.property.rating} />}
          </p>
        </div>
        <p className="flex-none text-right text-[15px] font-semibold tabular-nums text-heading">{formatMoney(b.priceMinor, b.currency)}</p>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        <span className="text-heading">{formatRange(b.checkIn, b.checkOut)}</span>
        <span className="text-muted-foreground">{nightsLabel(b.nights)}</span>
        {!cancelled && ph === 'current' && <span className="rounded-full bg-success/12 px-1.5 text-[11px] font-medium text-success">живут</span>}
        {cancelled && <span className="text-[12px] text-destructive">отменена</span>}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        {b.draft ? <DraftPill onClick={b.canEdit ? onEdit : undefined} /> : <span className="text-heading">{b.workerName}</span>}
        <span className="text-muted-foreground">подбирал: {b.manager?.displayName ?? '—'}</span>
        {b.account && <span className="text-[12px] text-muted-foreground">{b.account.title}</span>}
      </div>

      {(b.url || b.bookingUrl || b.chatUrl || b.canEdit) && (
        <div className="mt-2.5 flex items-center gap-1 border-t border-border/60 pt-2">
          {b.url && (
            <RowAction label="Открыть квартиру" onClick={() => onOpen(b.url!)}>
              <ExternalLink className="size-4" />
            </RowAction>
          )}
          {b.bookingUrl && (
            <RowAction label="Открыть бронь на сайте" onClick={() => onOpen(b.bookingUrl!)}>
              <FileText className="size-4" />
            </RowAction>
          )}
          {b.chatUrl && (
            <RowAction label="Переписка с хозяином" onClick={() => onOpen(b.chatUrl!)}>
              <MessageCircle className="size-4" />
            </RowAction>
          )}
          {b.canEdit && (
            <>
              <span className="flex-1" />
              <RowAction label="Изменить" onClick={onEdit}>
                <Pencil className="size-4" />
              </RowAction>
              <RowAction label="Удалить" danger onClick={onRemove}>
                <Trash2 className="size-4" />
              </RowAction>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RowAction({ label, onClick, children, danger }: { label: string; onClick: () => void; children: React.ReactNode; danger?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'grid size-8 place-items-center rounded-lg border-0 bg-transparent p-0 text-muted-foreground transition-colors hover:bg-secondary',
        danger ? 'hover:text-destructive' : 'hover:text-brand',
      )}
    >
      {children}
    </button>
  );
}
